
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Edit, Trash, Calendar, RefreshCw, Wifi, WifiOff, UserPlus } from 'lucide-react';
import { Doctor } from '@/types';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";

interface DoctorListProps {
  doctors: Doctor[];
  isLoading: boolean;
  onEditDoctor: (doctor: Doctor) => void;
  onDeleteDoctor: (doctor: Doctor) => void;
  onManageSchedule: (doctor: Doctor) => void;
  onRetryConnection?: () => void;
  hasConnectionError?: boolean;
}

const DoctorList: React.FC<DoctorListProps> = ({ 
  doctors, 
  isLoading, 
  onEditDoctor,
  onDeleteDoctor,
  onManageSchedule,
  onRetryConnection,
  hasConnectionError
}) => {
  // Function to handle connection retry with visual feedback
  const handleRetryConnection = async () => {
    try {
      toast.loading("Attempting to reconnect...", { id: "connection-retry" });
      
      // If parent component provided a retry handler, use it
      if (onRetryConnection) {
        await onRetryConnection();
        toast.success("Connection check complete", { id: "connection-retry" });
      }
    } catch (error) {
      toast.error("Connection failed", { id: "connection-retry" });
      console.error("Retry connection failed:", error);
    }
  };
  
  // If loading
  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Specialty</TableHead>
              <TableHead>Weekly Off Days</TableHead>
              <TableHead>Working Hours</TableHead>
              <TableHead className="w-[150px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell colSpan={5} className="text-center py-8">
                <div className="flex items-center justify-center space-x-2">
                  <RefreshCw className="h-4 w-4 animate-spin" />
                  <span>Loading doctors from database...</span>
                </div>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    );
  }
  
  // If connection error
  if (hasConnectionError && doctors.length === 0) {
    return (
      <Card className="bg-white rounded-lg shadow">
        <CardContent className="pt-6 text-center">
          <div className="flex flex-col items-center space-y-4 py-8">
            <div className="flex items-center gap-2 text-destructive">
              <WifiOff className="h-10 w-10" />
              <p className="text-lg font-medium">Unable to connect to database server</p>
            </div>
            <p className="text-muted-foreground max-w-md mx-auto">
              We couldn't connect to the database. This could be due to network issues or the database server being unavailable.
            </p>
            <Button 
              onClick={handleRetryConnection}
              className="flex gap-1 items-center mt-4"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry Connection
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // If connection is ok but no doctors found
  if (doctors.length === 0) {
    return (
      <Card className="bg-white rounded-lg shadow">
        <CardHeader>
          <CardTitle>No doctors found</CardTitle>
          <CardDescription>
            Your database is connected, but there are no doctors in the system yet.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center space-y-4 py-8">
            <div className="rounded-full bg-primary/10 p-6">
              <UserPlus className="h-12 w-12 text-primary" />
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-xl font-medium">Get Started with Doctor Management</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Click the "Add Doctor" button above to create your first doctor record, or use "Create Sample Records" to generate test data.
              </p>
              <Wifi className="h-5 w-5 text-green-500 mx-auto mt-4" />
              <p className="text-sm text-green-600">Database connection established</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // If connection error but we have cached doctors, still show them
  if (hasConnectionError && doctors.length > 0) {
    return (
      <div className="space-y-4">
        <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg flex items-center justify-between">
          <div className="flex items-center">
            <WifiOff className="h-5 w-5 text-amber-500 mr-2" />
            <span className="text-amber-800 font-medium">Connection issue detected</span>
          </div>
          <div className="text-sm text-amber-700">
            Showing cached doctor data
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRetryConnection}
              className="ml-3 bg-white border-amber-300 text-amber-700 hover:bg-amber-100"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry Connection
            </Button>
          </div>
        </div>
        
        {/* Show the doctors table with cached data */}
        <div className="bg-white rounded-lg shadow">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Specialty</TableHead>
                <TableHead>Weekly Off Days</TableHead>
                <TableHead>Working Hours</TableHead>
                <TableHead className="w-[150px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {doctors.map(doctor => (
                <TableRow key={doctor.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-10 w-10">
                        {doctor.image ? (
                          <AvatarImage src={doctor.image} alt={doctor.name} />
                        ) : (
                          <AvatarFallback>{doctor.name.substring(0, 2)}</AvatarFallback>
                        )}
                      </Avatar>
                      <span className="font-medium">{doctor.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{doctor.specialty}</TableCell>
                  <TableCell>
                    {doctor.weeklyOffDays && doctor.weeklyOffDays.length > 0 
                      ? doctor.weeklyOffDays.map(day => 
                          ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day]
                        ).join(', ')
                      : 'None'
                    }
                  </TableCell>
                  <TableCell>
                    {doctor.startTime && doctor.endTime 
                      ? `${doctor.startTime} - ${doctor.endTime}`
                      : '9:00 AM - 5:00 PM'
                    }
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => onEditDoctor(doctor)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => onManageSchedule(doctor)}
                      >
                        <Calendar className="h-4 w-4" />
                      </Button>
                      <Button 
                        size="icon" 
                        variant="ghost" 
                        onClick={() => onDeleteDoctor(doctor)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }
  
  // Normal case - connection is good and we have doctors
  return (
    <div className="bg-white rounded-lg shadow">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Name</TableHead>
            <TableHead>Specialty</TableHead>
            <TableHead>Weekly Off Days</TableHead>
            <TableHead>Working Hours</TableHead>
            <TableHead className="w-[150px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {doctors.map(doctor => (
            <TableRow key={doctor.id}>
              <TableCell>
                <div className="flex items-center gap-3">
                  <Avatar className="h-10 w-10">
                    {doctor.image ? (
                      <AvatarImage src={doctor.image} alt={doctor.name} />
                    ) : (
                      <AvatarFallback>{doctor.name.substring(0, 2)}</AvatarFallback>
                    )}
                  </Avatar>
                  <span className="font-medium">{doctor.name}</span>
                </div>
              </TableCell>
              <TableCell>{doctor.specialty}</TableCell>
              <TableCell>
                {doctor.weeklyOffDays && doctor.weeklyOffDays.length > 0 
                  ? doctor.weeklyOffDays.map(day => 
                      ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][day]
                    ).join(', ')
                  : 'None'
                }
              </TableCell>
              <TableCell>
                {doctor.startTime && doctor.endTime 
                  ? `${doctor.startTime} - ${doctor.endTime}`
                  : '9:00 AM - 5:00 PM'
                }
              </TableCell>
              <TableCell>
                <div className="flex space-x-2">
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    onClick={() => onEditDoctor(doctor)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    onClick={() => onManageSchedule(doctor)}
                  >
                    <Calendar className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="icon" 
                    variant="ghost" 
                    onClick={() => onDeleteDoctor(doctor)}
                  >
                    <Trash className="h-4 w-4" />
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default DoctorList;
